<template>
	<div class="products-time-picker">
		<h4 class="mb-2">{{ productName }}</h4>
		<div class="select-button d-flex flex-column"
			:class="[
				isPanelOpen ? 'rounded-t-lg' : 'rounded-lg',
				highlight ? 'highlight' : '',
			]"
			:style="{ 'outline-color': highlight  ? `${primaryColor}` : ''}"
			@click="togglePanel">
			<div :class="'d-flex justify-space-between align-center w-100' + (date ? '': ' v-card--disabled')" >
				<span v-if="!date">{{ $t('product.select_date') }}</span>
				<span v-else-if="!selectedTime">{{ $t('product.select_time') }}</span>
				<div v-else class="time-picker-label">
					<div>{{ selectedTime }}</div>
					<div v-if="isAnOngoingEvent && isFFOngoingLabelEnabled" class="ongoing-label" data-testid="ongoing-label">{{ $t('ongoing') }}</div>
				</div>
				<v-icon>{{ isPanelOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
			</div>
		</div>
		<div v-if="isPanelOpen"
			ref="selectionPanel"
			class="selection-panel rounded-b-lg">
			<v-skeleton-loader
				v-if="loading"
				type="list-item"
				class="my-4"
				:width="'100%'"
				:height="100"
			/>
			<template v-else>
				<template v-if="this.$vuetify.breakpoint.smAndUp">
					<div v-for="(slot, index) in timeSlots"
						:key="index"
						class="time-slot-item"
						@click="selectTime(slot)">
						<div class="period">{{ slot.period }}</div>
						<div v-if="!isForTimeBasedCombo"
							:class="['price', {'right-side': !isShowingAvailabilities }]">
								{{ $options.filters.currencyWithoutCountry(slot.price) }}
						</div>
						<div v-if="isFFOngoingLabelEnabled" class="ongoing-container"><div v-if="isOngoing(slot)" class="ongoing-label">{{ $t('ongoing') }}</div></div>
						<div v-if="isShowingAvailabilities" class="places-left" data-testid="time-picker-product-availabilities">
							{{ slot.places }} {{ $t('product.places_left') }}
						</div>
					</div>
				</template>
				<ProductsTimeScrollPicker v-else :time-slots="timeSlots" :is-date-selected="!!(date)" @select-time="selectTime" :display-price="!isForTimeBasedCombo"/>
			</template>
		</div>
	</div>
</template>

<script>
	import moment from 'moment';
	import globalVariables from "@/global";
	import {EComService} from '@connectngo/sdk';
	import ProductsTimeScrollPicker from '@/components/ProductV2/ProductsTimeScrollPicker.vue';

	export default {
		name: "ProductsTimePicker",
		components: {
			ProductsTimeScrollPicker
		},
		props: {
			product: {
				type: Object,
				required: true
			},
			date: {
				type: String,
			},
			productsSelected: {
				type: Map,
				required: true,
			},
		},
		data() {
			return {
				loading: true,
				isPanelOpen: false,
				selectedTime: null,
				timeSlots: [],
				productsSelectedArray: [],
				isAnOngoingEvent: false
			};
		},
		computed: {
			eventGroupId() {
				return this.product?.data?.event_group_id ;
			},
			productName() {
				const productName = this.$options.filters.translatable(
						this.product.data.name,
						this.product.data.name_i18n,
						this.$i18n.locale
					);

				if(!this.product.data.parent_combo_product) {
					return productName;
				}

				const parentComboName = this.$options.filters.translatable(
					this.product.data.parent_combo_product.data.name,
					this.product.data.parent_combo_product.data.name_i18n,
					this.$i18n.locale
				)

				return `${parentComboName}: ${productName}`

			},
			highlight() {
				return !this.isPanelOpen && !this.selectedTime && this.date;
			},
			primaryColor() {
				return this.$vuetify.theme.isDark
					? this.$vuetify.theme.themes.dark.primary
					: this.$vuetify.theme.themes.light.primary
			},
			isForTimeBasedCombo() {
				return 'parent_combo_product' in this.product.data
			},
			comboProductsForEventGroup() {
				return this.productsSelectedArray.filter(({ product }) => product.data.is_combo
					&& product.data.combo_products.some(comboProduct => comboProduct.event_group_id === this.product.data.event_group_id && comboProduct.id === this.product.data.id));
			},
			isForTimeBasedModifiers() {
				return this.modifiersProductsForEventGroup && this.modifiersProductsForEventGroup.length > 0
			},
			modifiersProductsForEventGroup() {
				return this.productsSelectedArray.filter(({ modifiers }) => {
					if (modifiers && modifiers.size > 0) {
						// To support mobile devices we need to check if modifiers is a Map/Set (use size and values method) or a plain object
						const valuesArray = (modifiers instanceof Map || modifiers instanceof Set)
							? Array.from(modifiers.values())  // For Map or Set
							: Object.values(modifiers);       // For regular object

						// Now we can safely call .some() on the values array
						return valuesArray.some(({ modifier }) =>
							modifier.event_group_id === this.product.data.event_group_id &&
							modifier.id === this.product.data.id
						);
					}
					return false; // If modifiers is not valid or size is 0, return false
				});

			},
			hasTimeBasedComboChildToSync() {
				if(!this.isForTimeBasedCombo) {
					return false;
				}

				const comboProducts = this.product.data.parent_combo_product.data.combo_products;

				return comboProducts.some(product => {
					return product.id !== this.product.data.id
						&& product.event_group_id === this.product.data.event_group_id
				})
			},
			isShowingAvailabilities() {
				return this.$root.websiteConfig.data.fields.show_tickets_availabilities === "1";
			},
			isFFOngoingLabelEnabled() {
				const { feature_flags } = this.$root.websiteConfig.data || {};
				return feature_flags.includes('co-9680-ongoing-label');
			}
		},
		mounted() {
			this.productsSelectedArray = Array.from(this.productsSelected.values());
			if(this.date) {
				this.fetchAvailabilities();
			}

			// Event for combo child event group sync
			if (this.hasTimeBasedComboChildToSync) {
				this.$root.$on('comboEventSelected', this.syncComboEventForEventGroup)
			}
		},
		beforeDestroy () {
			if (this.hasTimeBasedComboChildToSync) {
				this.$root.$off('comboEventSelected', this.syncComboEventForEventGroup)
			}
		},
		watch : {
			date(newVal) {
				if(newVal === null) {
					this.isPanelOpen = false;
				}
				this.selectedTime = null;
				this.fetchAvailabilities();
			},
		},
		methods: {
			fetchAvailabilities() {
				this.loading = true;

				let startDate;
				if(!this.product.data.unavailable_before) {
					const isToday = moment(this.date).isSame(new Date(), "day");
					startDate = isToday ?
						this.$options.filters.dateToTenantTimezone().startOf('day').unix() :
						this.$options.filters.dateToTenantTimezone(moment(this.date)).startOf('day').unix();
				} else {
					startDate = this.product.data.unavailable_before
				}

				const endDate = this.$options.filters.dateToTenantTimezone(
					moment(this.date).endOf('day')
				).unix();

				new EComService().getEventAvailabilities(this.eventGroupId, startDate, endDate, this.product.data.id)
					.then(response => { this.formatDataForSelect(response)})
					.catch(error => this.$handleError(this, error))
					.finally(() => (this.loading = false));
			},
			formatDataForSelect(datas) {
				this.timeSlots = [];
				const targetEventGroupId = this.product.data.event_group_id;
				const targetProductId = this.product.data.id;
				const requiredSharedQuantity = this.getEventSharedQuantity(targetProductId, targetEventGroupId)

				datas.forEach(( eventAvailability ) => {
					if (eventAvailability.data.day === this.date) {
						eventAvailability.data.events.forEach(( event ) => {
							if (event.data.available < requiredSharedQuantity) {
								return;
							}
							if (event.data.price) {
								this.timeSlots.push({
									period: event.data.period,
									places: event.data.available,
									price: event.data.price?.min_price,
									start_ts: event.data.start_ts,
									end_ts: event.data.end_ts,
									event_id : event.data.id
								});
							}
						});
					}
				});
				if (this.timeSlots.length === 1) {
					this.selectTime(this.timeSlots[0]);
				}
			},
			togglePanel() {
				if (!this.date) {
					return;
				}
				this.isPanelOpen = !this.isPanelOpen;
			},
			selectTime(slot, sync = true) {
				this.selectedTime = slot.period;
				this.isPanelOpen = false;
				this.isAnOngoingEvent = this.isOngoing(slot)

				this.validateEventPrice(slot, sync);
			},
			validateEventPrice(slot, sync = true) {
				new EComService().getProductPrices(slot.event_id, [this.product.data.id])
					.then(res => {
						this.$emit("timeSelected", {
							period: slot.period,
							places: slot.places,
							start_ts: slot.start_ts,
							end_ts: slot.end_ts,
							event_id: slot.event_id,
							price : res.data[this.product.data.id]?.min_price
						});

						if (sync && this.hasTimeBasedComboChildToSync) {
							this.$root.$emit('comboEventSelected', this.product, slot)
						}
					})
					.catch(err => this.$handleError(this, err))
			},
			getEventSharedQuantity(productId, eventGroupId) {
				let requiredSharedQuantity = this.productsSelectedArray
					.filter(({ product }) => product.data.event_group_id === eventGroupId && product.data.id === productId)
					.reduce((sum, product) => sum + product.quantity, 0);

				if (this.isForTimeBasedCombo) {
					requiredSharedQuantity += this.getComboSharedQuantity(productId, eventGroupId)
				}
				if(this.isForTimeBasedModifiers) {
					requiredSharedQuantity += this.getModifiersSharedQuantity(productId, eventGroupId)
				}

				return requiredSharedQuantity;
			},
			getComboSharedQuantity(productId, eventGroupId) {
				let quantity = 0;
				this.comboProductsForEventGroup.forEach(product => {
					const combosForEventGroup = product.product.data.combo_products
						.filter(comboProduct => comboProduct.event_group_id === eventGroupId && comboProduct.id === productId)

					combosForEventGroup.forEach(combo => {
						const comboQty = product.product.data.combo_quantities
							.find(quantity => quantity.product_id === combo.id).quantity
						quantity += product.quantity * comboQty;
					})
				})
				return quantity;
			},
			getModifiersSharedQuantity(productId, eventGroupId) {
				const ffEnabled = globalVariables.websiteConfig.featureFlagEnabled("co-8590-remove-modifier-multiplicator");
				let quantity = 0;

				this.modifiersProductsForEventGroup.forEach(product => {
					// Ensure we are working with an array, whether product.modifiers is an iterable or not
					const modifiersArray = Array.isArray(product.modifiers)
						? product.modifiers // Already an array
						: Object.values(product.modifiers); // Convert to array if it's an object

					const modifier = modifiersArray
						.find(({ modifier }) => modifier.event_group_id === eventGroupId && modifier.id === productId);

					if (modifier) {
						quantity += ffEnabled ? modifier.quantity : product.quantity * modifier.quantity;
					}
				});

				return quantity;

			},
			syncComboEventForEventGroup(comboProduct, slot) {
				if(!this.hasTimeBasedComboChildToSync || !this.mustSyncComboEvent(comboProduct)) {
					return;
				}

				return this.selectTime(slot, false)
			},
			mustSyncComboEvent(comboProduct) {
				const hasMoreThanOneEvent = this.timeSlots.length > 1; // Auto-select already made if 1 event only
				const isNotTheCurrentProduct = comboProduct.data.id !== this.product.data.id;
				const isFromSameComboProduct =  comboProduct.data?.parent_combo_product_id === this.product.data?.parent_combo_product_id;
				const isProductForSameEventGroup = comboProduct.data.event_group_id === this.product.data.event_group_id;

				return hasMoreThanOneEvent && isNotTheCurrentProduct && isFromSameComboProduct && isProductForSameEventGroup;
			},
			isOngoing(slot){
				const startTime = this.$options.filters.dateToTenantTimezone(moment.unix(slot.start_ts)).unix();
				const currentTime = this.$options.filters.dateToTenantTimezone(moment()).unix();
				const endTime = this.$options.filters.dateToTenantTimezone(moment.unix(slot.end_ts)).unix();
				return currentTime >= startTime && currentTime <= endTime
			}
		},
	};
</script>

<style lang="scss" scoped>
	.products-time-picker {
		position: relative;
		width: 100%;
	}
	.time-picker-label{
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.select-button {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 10px 16px;
		border: 1px solid $grey-light;
		cursor: pointer;
		background-color: white;
	}
	.selection-panel {
		background-color: white;
		overflow: hidden;
		transition: height 0.3s ease-out;
		border: 1px solid $grey-light;
		border-top: none;
		height: auto;
		width: 100%;
	}
	.selection-panel.pt-3 {
		padding-top: 16px;
	}
	.time-slot-item {
		display: flex;
		gap: 8px;
		justify-content: space-between;
		align-items: center;
		padding: 8px 16px;
		cursor: pointer;
		margin: 0;
	}
	.time-slot-item:hover {
		background-color: #f0f0f0;
	}
	.period{
		font-weight: bold;
		width: 165px;
	}
	.right-side {
		order:1
	}
	.places-left {
		color: gray;
		font-size: 0.9rem;
	}
	.highlight {
		outline-style: solid;
		outline-width: 2px;
		transition: outline 0.1s ease;
	}
	.ongoing{
		&-container{
			width: 100px
		}
		&-label{
			background-color:#FECAAA;
			border-radius: 16px;
			display: inline-block;
			padding: 4px 12px;
		}
	}
</style>
